import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip,
  Popover,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SpeedIcon from '@mui/icons-material/Speed';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format } from 'date-fns';
import KPICard from '../components/common/KPICard';
import DateRangePicker, { getDefaultDateRange, formatDateRange } from '../components/common/DateRangePicker';
import ExportButton from '../components/common/ExportButton';
import RevenueChart from '../components/charts/RevenueChart';
import { useOverview, useTrailerEconomics, useRevenueByPeriod, useCreateCost } from '../hooks/useApi';
import type { DateRange, TrailerEconomics, ManualCost } from '../types';

type SortField = 'trailerNumber' | 'revenue' | 'kilometers' | 'profit' | 'revenuePerKm';
type SortOrder = 'asc' | 'desc';

export default function Dashboard() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [sortField, setSortField] = useState<SortField>('profit');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [costAnchor, setCostAnchor] = useState<HTMLButtonElement | null>(null);
  const [costTrailer, setCostTrailer] = useState<{ id: string; number: string } | null>(null);
  const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);
  const [newCost, setNewCost] = useState({
    amount: '',
    description: '',
    category: 'OTHER' as ManualCost['category'],
  });
  const { from, to } = formatDateRange(dateRange);

  const { data: overview, isLoading: isLoadingOverview, error: overviewError } = useOverview(from, to);
  const { data: economicsData, isLoading: isLoadingEconomics } = useTrailerEconomics(from, to);
  const { data: revenueData, isLoading: isLoadingRevenue } = useRevenueByPeriod(from, to, 'day');
  const createCost = useCreateCost();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number, decimals = 0) => {
    return new Intl.NumberFormat('da-DK', {
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const handleTrailerClick = (trailerId: string) => {
    navigate(`/trailers/${trailerId}`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleOpenCostPopover = (event: React.MouseEvent<HTMLButtonElement>, trailer: { id: string; number: string }) => {
    event.stopPropagation();
    setCostAnchor(event.currentTarget);
    setCostTrailer(trailer);
    setNewCost({ amount: '', description: '', category: 'OTHER' });
  };

  const handleCloseCostPopover = () => {
    setCostAnchor(null);
    setCostTrailer(null);
  };

  const handleAddCost = async () => {
    if (!costTrailer || !newCost.amount) return;
    await createCost.mutateAsync({
      trailerId: costTrailer.id,
      amount: parseFloat(newCost.amount),
      description: newCost.description,
      category: newCost.category,
      date: format(new Date(), 'yyyy-MM-dd'),
    });
    handleCloseCostPopover();
  };

  const sortedTrailers = economicsData?.trailers
    ? [...economicsData.trailers].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      })
    : [];

  // Calculate averages for context
  const avgRevenuePerKm = economicsData?.trailers?.length
    ? economicsData.trailers.reduce((sum, t) => sum + t.revenuePerKm, 0) / economicsData.trailers.length
    : 0;

  if (overviewError) {
    return (
      <Box>
        <Alert severity="error">
          Kunne ikke hente data. Kontroller at din organisation har en gyldig DORA API-nøgle.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <ExportButton from={from} to={to} />
        </Box>
      </Box>

      {isLoadingOverview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : overview ? (
        <>
          {/* Summary KPI Cards */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <KPICard
                title="Omsætning"
                value={formatCurrency(overview.totalRevenue)}
                icon={<AttachMoneyIcon />}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KPICard
                title="Profit"
                value={formatCurrency(overview.totalProfit)}
                icon={<TrendingUpIcon />}
                color={overview.totalProfit >= 0 ? 'success' : 'error'}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KPICard
                title="Køretøjer"
                value={overview.trailerCount}
                subtitle={`${formatNumber(overview.utilizationRate, 1)}% udn.`}
                icon={<LocalShippingIcon />}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KPICard
                title="Gns. kr/km"
                value={`${formatNumber(avgRevenuePerKm, 2)} kr`}
                subtitle={`${formatNumber(overview.totalKilometers)} km total`}
                icon={<SpeedIcon />}
                color="warning"
              />
            </Grid>
          </Grid>

          {/* Revenue Chart */}
          {isLoadingRevenue ? (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              </CardContent>
            </Card>
          ) : revenueData ? (
            <Box sx={{ mb: 2 }}>
              <RevenueChart data={revenueData} title="Omsætning over tid" height={200} />
            </Box>
          ) : null}

          {/* Vognregnskab (Trailer Economics Table) */}
          <Card>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Vognregnskab
                </Typography>
                <Tooltip title="Datakilde information">
                  <IconButton size="small" onClick={(e) => setInfoAnchor(e.currentTarget)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {isLoadingEconomics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sortField === 'trailerNumber'}
                            direction={sortField === 'trailerNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('trailerNumber')}
                          >
                            Køretøj
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sortField === 'revenue'}
                            direction={sortField === 'revenue' ? sortOrder : 'desc'}
                            onClick={() => handleSort('revenue')}
                          >
                            Omsætning
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sortField === 'kilometers'}
                            direction={sortField === 'kilometers' ? sortOrder : 'desc'}
                            onClick={() => handleSort('kilometers')}
                          >
                            Km
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sortField === 'revenuePerKm'}
                            direction={sortField === 'revenuePerKm' ? sortOrder : 'desc'}
                            onClick={() => handleSort('revenuePerKm')}
                          >
                            Kr/km
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          <TableSortLabel
                            active={sortField === 'profit'}
                            direction={sortField === 'profit' ? sortOrder : 'desc'}
                            onClick={() => handleSort('profit')}
                          >
                            Profit
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: 50 }}>
                          Omk.
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTrailers.map((trailer: TrailerEconomics) => (
                        <TableRow
                          key={trailer.trailerId}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleTrailerClick(trailer.trailerId)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {trailer.trailerNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(trailer.revenue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(trailer.kilometers)}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${formatNumber(trailer.revenuePerKm, 2)} kr`}
                              size="small"
                              color={trailer.revenuePerKm >= avgRevenuePerKm ? 'success' : 'default'}
                              variant={trailer.revenuePerKm >= avgRevenuePerKm ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color={trailer.profit >= 0 ? 'success.main' : 'error.main'}
                            >
                              {formatCurrency(trailer.profit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Tilføj omkostning">
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenCostPopover(e, { id: trailer.trailerId, number: trailer.trailerNumber })}
                              >
                                <AddCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {sortedTrailers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Ingen køretøjer fundet i perioden
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Add Cost Popover */}
          <Popover
            open={Boolean(costAnchor)}
            anchorEl={costAnchor}
            onClose={handleCloseCostPopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, width: 280 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Tilføj omkostning til {costTrailer?.number}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <TextField
                  label="Beløb (DKK)"
                  type="number"
                  size="small"
                  value={newCost.amount}
                  onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Beskrivelse"
                  size="small"
                  value={newCost.description}
                  onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Kategori</InputLabel>
                  <Select
                    value={newCost.category}
                    label="Kategori"
                    onChange={(e) => setNewCost({ ...newCost, category: e.target.value as ManualCost['category'] })}
                  >
                    <MenuItem value="FUEL">Brændstof</MenuItem>
                    <MenuItem value="MAINTENANCE">Vedligehold</MenuItem>
                    <MenuItem value="INSURANCE">Forsikring</MenuItem>
                    <MenuItem value="OTHER">Andet</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                  <Button size="small" onClick={handleCloseCostPopover}>Annuller</Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddCost}
                    disabled={!newCost.amount || createCost.isPending}
                  >
                    Tilføj
                  </Button>
                </Box>
              </Box>
            </Box>
          </Popover>

          {/* Info Popover */}
          <Popover
            open={Boolean(infoAnchor)}
            anchorEl={infoAnchor}
            onClose={() => setInfoAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, maxWidth: 360 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Hvor kommer tallene fra?
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">Omsætning</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sum af alle fakturalinjer fra DORA API for ordrer tilknyttet køretøjets ruter i perioden.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">Kilometer</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Beregnet fra odometer-aflæsninger (slut - start) på hver rute fra DORA API.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">Kr/km</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Omsætning divideret med kørte kilometer. Grøn = over gennemsnit.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">Profit</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Omsætning minus omkostninger (brændstof + manuelle omkostninger). Brændstof beregnes ud fra km/L og en fast literpris.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">Manuelle omkostninger</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Omkostninger du selv tilføjer via + knappen. Gemmes lokalt i DORA Insight.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Popover>
        </>
      ) : null}
    </Box>
  );
}
